// =============================================================================
// Error-catalogue reporting (frontend-raised errors) — Phase 3 of the
// error-code catalogue (mofsl-ekyc-backend docs/features/analytics-and-telemetry/
// 2026-09-11-error-code-catalog-trd.md §6).
//
// Product catalogued every error/drop in ekycv3_error_drop_code.xlsx. Backend-
// raised errors are recorded server-side (ApiError.catalogCode). Frontend-raised
// ones — validation copy, file-upload checks, session expiry, network failures —
// only exist in the browser, so this module reports them as an `error_shown`
// telemetry event that TelemetryIngestService routes into ekyc.user_error_events.
//
// Hard contract (same as telemetry.js): this MUST NEVER block or break the
// journey. Every export swallows every error and returns undefined.
//
// Two ways to identify the error:
//   reportError(ERROR_CATALOG.GLOBAL_SESSION_SD_FE_01)   — by catalogue code
//   reportError('Please enter a valid mobile number')    — by the exact user-
//     facing message; resolved through MESSAGE_TO_CODE (generated from the
//     workbook). Unknown messages are a no-op unless opts.fallbackCode is given.
//
// Display-time hooks call reportError(message): GlobalSnackbar (toasts),
// InputBox (field errors), FileUpload (upload errors). Consecutive duplicates
// (same code within DEDUPE_MS) are collapsed so a re-render does not double-count.
// =============================================================================

import { ERROR_CATALOG, MESSAGE_TO_CODE } from './errorCatalog';
import { trackNamed } from './telemetry';

export const ERROR_SHOWN_EVENT = 'error_shown';
const DEDUPE_MS = 2000;
const CODE_SHAPE = /^[A-Z0-9_]{1,40}$/;
const SCREEN_SHAPE = /^[A-Za-z0-9_.-]{1,100}$/;

const lastSeen = new Map(); // code -> timestamp of last report

const normalise = (s) => String(s).replace(/\s+/g, ' ').trim();

/** Resolve a catalogue code from a code or an exact user-facing message. Null when unknown. */
export const resolveErrorCode = (codeOrMessage) => {
    if (codeOrMessage == null) return null;
    const text = normalise(codeOrMessage);
    if (!text) return null;
    if (CODE_SHAPE.test(text) && ERROR_CATALOG[text]) return text;
    return MESSAGE_TO_CODE[text] || null;
};

/** Current screen id from the route, in the shape the BE accepts (or undefined). */
export const currentScreenId = () => {
    try {
        const seg = (window.location.pathname || '').split('/').filter(Boolean).pop() || '';
        return SCREEN_SHAPE.test(seg) ? seg : undefined;
    } catch (_) {
        return undefined;
    }
};

const shouldReport = (code, now) => {
    const prev = lastSeen.get(code);
    if (prev != null && now - prev < DEDUPE_MS) return false;
    lastSeen.set(code, now);
    return true;
};

/**
 * Report a frontend-raised catalogue error.
 * @param {string} codeOrMessage  catalogue code or the exact message shown
 * @param {object} [opts]
 * @param {string} [opts.fallbackCode]  code to use when the message is not in the map
 * @param {string} [opts.detail]        short cause (field name, file type, url) — max 200 chars server-side
 * @param {string} [opts.correlationId] BE correlation id when the error was a reaction to an API failure
 * @param {number} [opts.stage]         journey stage; resolved from the screen when omitted
 * @param {string} [opts.screenId]      screen id; defaults to the last route segment
 */
export const reportError = (codeOrMessage, opts = {}) => {
    try {
        const code = resolveErrorCode(codeOrMessage)
            || (opts.fallbackCode && ERROR_CATALOG[opts.fallbackCode] ? opts.fallbackCode : null);
        if (!code) return undefined;
        if (!shouldReport(code, Date.now())) return undefined;

        const metadata = { errorCode: code };
        if (opts.correlationId) metadata.correlationId = String(opts.correlationId).slice(0, 64);
        if (opts.detail) metadata.detail = normalise(opts.detail).slice(0, 200);
        else if (code !== normalise(codeOrMessage)) metadata.detail = normalise(codeOrMessage).slice(0, 200);

        trackNamed(ERROR_SHOWN_EVENT, opts.stage, opts.screenId || currentScreenId(), { metadata });
    } catch (_) {
        /* swallow — never break the journey */
    }
    return undefined;
};

/** Report an ApiError / NetworkError thrown by services/index.js (network failures only — BE errors are recorded server-side). */
export const reportNetworkError = (err) => {
    try {
        if (err && err.name === 'NetworkError') {
            reportError(ERROR_CATALOG.GLOBAL_NETWORK_SD_FE_06, { detail: err.url });
        }
    } catch (_) {
        /* swallow */
    }
};

/** Test hook: forget dedupe state. */
export const __resetErrorReportForTests = () => lastSeen.clear();

export default reportError;
